import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { useTable } from 'react-table';

import { Check as CheckIcon } from '@styled-icons/boxicons-regular/Check';
import { Close as CloseIcon } from '@styled-icons/evaicons-solid/Close';
import { api } from 'services/api';
import { toast } from 'shared/toast';

import {
  TableContainer,
  Box,
  HStack,
  Button,
  Icon,
  Text,
} from '@chakra-ui/react';

import Loader from 'components/Loader';
import PaginationWrapper from 'components/Pagination';
import { Table, Tbody, Td, Th, Thead, Tr } from 'components/Table';

import useThrottledState from 'hooks/useThrottledState';

import { CompositionGroup } from 'types/composition';
import { Pagination, ServicePagination } from 'types/pagination';

import { ROUTE_BY_RESOURCE_TYPE, FILTER_KEY_BY_RESOURCE_TYPE } from './shared';
import { Row } from './types';

type Level2GroupsTableProps = {
  resourceType: 'service' | 'input';
  parentGroup: CompositionGroup;
  onSelectGroup: (parent: CompositionGroup, group: CompositionGroup) => void;
  onRemoveGroup?: (parent: CompositionGroup, group: CompositionGroup) => void;
  isGroupSelected?: (
    parent: CompositionGroup,
    group: CompositionGroup,
  ) => boolean;
  multiSelect?: boolean;
};

const NON_SELECTABLE_PARENT_GROUP_KEY = '00';
const NON_SELECTABLE_PARENT_GROUP_DESC = 'Serviço';

const Level2GroupsTable: React.FC<Level2GroupsTableProps> = ({
  resourceType,
  parentGroup,
  onSelectGroup,
  onRemoveGroup,
  isGroupSelected,
  multiSelect = false,
}) => {
  const [level2Groups, setLevel2Groups] = useState<CompositionGroup[]>([]);
  const [loading, setLoading] = useState(false);

  const [pagination, setPagination] = useThrottledState<Pagination>(
    (() => {
      return {
        per_page: 10,
        page: 1,
      };
    })(),
    1000,
  );

  const [servicePagination, setServicePagination] = useState<ServicePagination>(
    { last_page: 1 },
  );

  const getData = useCallback(async () => {
    setLoading(true);
    setLevel2Groups([]);

    try {
      const response = await api.get(ROUTE_BY_RESOURCE_TYPE[resourceType], {
        params: {
          'filter[level]': 2,
          'filter[status]': 'ACTIVE',
          page: pagination.page,
          per_page: pagination.per_page,
          [`filter[${FILTER_KEY_BY_RESOURCE_TYPE[resourceType]}][0]`]:
            parentGroup.key,
        },
      });

      const componentsData = response.data;
      const newPagination = {
        last_page: componentsData.meta?.last_page || 1,
      };

      setLevel2Groups(componentsData.data);
      setServicePagination(newPagination);
    } catch (err) {
      setLevel2Groups([]);
      setServicePagination({ last_page: 1 });

      toast({
        status: 'error',
        description:
          err.response?.data?.message ||
          'Houve um erro ao buscar os grupos de serviço',
      });
    } finally {
      setLoading(false);
    }
  }, [pagination, parentGroup, resourceType]);

  useEffect(() => {
    getData();
  }, [getData]);

  const columns = useMemo(
    () => [
      {
        Header: 'Código',
        id: 'expander',
        width: '1%',
        Cell: ({ row }: { row: Row }) => {
          return (
            <Text ml="1" fontWeight="bold">
              {row.original.key}
            </Text>
          );
        },
      },
      {
        Header: 'Descrição',
        width: '90%',
        accessor: 'data.description',
        Cell: ({ row }: { row: Row }) => {
          return row.original.description || '-';
        },
      },
      {
        Header: 'Ações',
        width: '9%',
        accessor: 'actions',
        isCentered: true,
        Cell: ({ row }: { row: Row }) => {
          const isSelected = Boolean(
            multiSelect && isGroupSelected?.(parentGroup, row.original),
          );

          const handleClick = (): void => {
            if (multiSelect) {
              if (isSelected) {
                onRemoveGroup?.(parentGroup, row.original);
                return;
              }

              onSelectGroup(parentGroup, row.original);
              return;
            }

            onSelectGroup(parentGroup, row.original);
          };

          return (
            <HStack
              spacing="2"
              justifyContent={{
                base: 'flex-start',
                md: 'center',
              }}
            >
              <Button
                colorScheme={isSelected ? 'red' : 'green'}
                leftIcon={<Icon as={isSelected ? CloseIcon : CheckIcon} />}
                isDisabled={
                  resourceType === 'input' &&
                  parentGroup.key === NON_SELECTABLE_PARENT_GROUP_KEY &&
                  parentGroup.description === NON_SELECTABLE_PARENT_GROUP_DESC
                }
                onClick={handleClick}
              >
                {multiSelect && isSelected ? 'Remover' : 'Selecionar'}
              </Button>
            </HStack>
          );
        },
      },
    ],
    [
      isGroupSelected,
      multiSelect,
      onRemoveGroup,
      onSelectGroup,
      parentGroup,
      resourceType,
    ],
  );

  const getRowId = useCallback((row) => {
    return `${row.key}-${row.description}`;
  }, []);

  const { getTableProps, getTableBodyProps, rows, prepareRow, visibleColumns } =
    useTable({
      // @ts-expect-error types
      columns,
      data: level2Groups,
      getRowId,
    });

  return (
    <Box>
      <TableContainer width="100%">
        <Table {...getTableProps()}>
          <Thead>
            <Tr inHeader>
              <Th width="1%">Código</Th>
              <Th width="90%">Descrição</Th>
              <Th width="9%">Ações</Th>
            </Tr>
          </Thead>

          <Tbody {...getTableBodyProps()}>
            {!loading &&
              rows.length > 0 &&
              rows.map((row) => {
                prepareRow(row);

                return (
                  <React.Fragment key={row.id}>
                    <Tr inHeader={false} {...row.getRowProps()}>
                      {row.cells.map((cell, idx) => (
                        <Td
                          columnKey={idx}
                          {...cell.getCellProps()}
                          // @ts-expect-error isNumeric does not exists on lib type
                          isNumeric={cell.column.isNumeric}
                        >
                          {cell.render('Cell')}
                        </Td>
                      ))}
                    </Tr>
                  </React.Fragment>
                );
              })}

            {loading && (
              <Tr inHeader={false}>
                <Td
                  columnKey={1}
                  colSpan={visibleColumns.length}
                  borderBottom="none"
                  background="gray.100"
                >
                  <Loader />
                </Td>
              </Tr>
            )}

            {!loading && level2Groups.length === 0 && (
              <Tr inHeader={false}>
                <Td
                  columnKey={1}
                  colSpan={visibleColumns.length}
                  borderBottom="none"
                  background="gray.100"
                  className="text-center"
                >
                  Nenhum resultado encontrado.
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>

      <PaginationWrapper
        lastPage={servicePagination.last_page}
        onPaginate={(selectedPage) => {
          setPagination({ ...pagination, page: selectedPage });
        }}
      />
    </Box>
  );
};

export default Level2GroupsTable;
